import React, { useCallback, useRef, useEffect, Key } from "react";
import { useLocation } from "react-router";
import { useNotification, motion, Flex, Icon, Icons, Paragraph, Button } from "@ir/ui";
import { useEventBusEmitter, useSocketListener, useUser } from "@/hooks";
import { WebSocketAction, WebSocketResponse } from "@/core/interface/WebSocketResponse";
import { NewConversationMessagePayload, NotificationEvents } from "@/core/events/notificationEventsMap";
import { JsonSerializer } from "@/infrastructure/serializers/JsonSerializer";
import { TabType } from "@/store/deduction/drawer";
import { WORKSPACE_ROUTES } from "@/routes/workspace/utils/Workspace.router.utils";
import { useDeductionDrawer } from "@/presentation/deduction/hooks/drawer/useDeductionDrawer";
import { useConversations } from "@/presentation/pages/conversations/hooks/useConversations";
import { Conversation } from "@/core/entities/conversation";
import { getMessageStringFromQuill } from "@/presentation/pages/conversations/utils/textHelpers";
import { useRemittanceItem } from "@/presentation/deduction/hooks";
import { createLogger } from "@/infrastructure/services/logger";
import { CategoryKey } from "@/core/entities/deduction/remittance/RemittanceItem";

const jsonSerializer = new JsonSerializer();

/**
 * Custom hook to handle conversation notifications
 * Manages WebSocket listeners, notification display logic, and conversation updates
 */
export const useConversationNotifications = (): { contextHolder: React.ReactNode } => {
  const logger = createLogger("useConversationNotifications");
  const { user } = useUser();
  const [{ open, close }, contextHolder] = useNotification({ motion, stack: true, pauseOnHover: true });
  const { emit } = useEventBusEmitter();
  const location = useLocation();
  const { setDrawerInfo, setDrawerOpen, setSelectedTab, selectedTab, drawerInfo, setCurrentExpandedDeductionId } = useDeductionDrawer();
  const { addOrUpdateConversation, setCurrentConversation, incrementNewMessageCount } = useConversations();
  const { getRemittanceItemById } = useRemittanceItem();

  // Use refs to store latest values for use in callbacks
  const locationRef = useRef(location);
  const selectedTabRef = useRef(selectedTab);
  const drawerInfoRef = useRef(drawerInfo);

  // Update refs when values change
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    selectedTabRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    drawerInfoRef.current = drawerInfo;
  }, [drawerInfo]);

  const addOrUpdateConversationHandler = useCallback(
    (newConversationMessagePayload: NewConversationMessagePayload) => {
      addOrUpdateConversation(
        new Conversation({
          id: newConversationMessagePayload.conversationId,
          remittanceItemId: newConversationMessagePayload.remittanceItemId,
          categoryKey: newConversationMessagePayload.categoryKey,
          categoryTypeKey: newConversationMessagePayload.categoryTypeKey,
          checkNumber: newConversationMessagePayload.checkNumber,
          invoiceNumber: newConversationMessagePayload.invoiceNumber,
          referenceId: newConversationMessagePayload.referenceId,
          createdAt: newConversationMessagePayload.createdAt,
          updatedAt: new Date().toISOString(),
          participants: newConversationMessagePayload.participants,
          purchaserKey: newConversationMessagePayload.purchaserKey,
          purchaserName: newConversationMessagePayload.purchaserName,
          lastMessage: {
            attachmentCount: newConversationMessagePayload.attachments?.length || 0,
            messageId: newConversationMessagePayload.id,
            sender: newConversationMessagePayload.sender,
            messagePreview: getMessageStringFromQuill(newConversationMessagePayload.contentHtml) ?? "",
          },
          unreadMessageCount: 1,
        })
      );
    },
    [addOrUpdateConversation]
  );

  const fetchRemittanceItem = useCallback(
    async (remittanceItemId: number) => {
      return await getRemittanceItemById(remittanceItemId);
    },
    [getRemittanceItemById]
  );

  const handleViewConversation = useCallback(
    async (notificationKey: Key, newConversationMessagePayload: NewConversationMessagePayload) => {
      close(notificationKey);
      const remittanceItem = await fetchRemittanceItem(newConversationMessagePayload.remittanceItemId);
      setCurrentExpandedDeductionId(newConversationMessagePayload.referenceId);
      setDrawerInfo({
        nextPrevEnabled: false,
        checkNumber: newConversationMessagePayload?.checkNumber,
        invoiceNumber: newConversationMessagePayload?.invoiceNumber,
        remittanceItemId: newConversationMessagePayload?.remittanceItemId,
        purchaserKey: newConversationMessagePayload?.purchaserKey ?? "",
        isDeductionIncomplete: newConversationMessagePayload.categoryKey === CategoryKey.REMITTANCE_ITEM,
        isManuallyUploaded: false,
        isRemittanceItemsPage: false,
        remittanceItem: remittanceItem,
      });
      setSelectedTab(TabType.CONVERSATIONS);

      const currentPathname = locationRef.current.pathname;
      if (currentPathname === `/workspace${WORKSPACE_ROUTES.CONVERSATIONS}`) {
        setCurrentConversation(
          new Conversation({
            id: newConversationMessagePayload.conversationId,
            remittanceItemId: newConversationMessagePayload.remittanceItemId,
            categoryTypeKey: newConversationMessagePayload.categoryTypeKey,
            purchaserKey: newConversationMessagePayload.purchaserKey,
            purchaserName: newConversationMessagePayload.purchaserName,
            checkNumber: newConversationMessagePayload.checkNumber,
            invoiceNumber: newConversationMessagePayload.invoiceNumber,
            referenceId: newConversationMessagePayload.referenceId,
            categoryKey: newConversationMessagePayload.categoryKey,
            createdAt: newConversationMessagePayload.createdAt,
            updatedAt: new Date().toISOString(),
            participants: newConversationMessagePayload.participants,
            lastMessage: {
              attachmentCount: newConversationMessagePayload.attachments?.length || 0,
              messageId: newConversationMessagePayload.id,
              sender: newConversationMessagePayload.sender,
              messagePreview: getMessageStringFromQuill(newConversationMessagePayload.contentHtml) ?? "",
            },
            unreadMessageCount: 1,
          })
        );
      } else {
        setDrawerOpen(true);
      }
    },
    [close, fetchRemittanceItem, setDrawerInfo, setSelectedTab, setCurrentConversation, setDrawerOpen, setCurrentExpandedDeductionId]
  );

  const shouldShowNotification = useCallback(
    (newConversationMessagePayload: NewConversationMessagePayload): boolean => {
      // Use refs to get the latest values
      const currentPathname = locationRef.current.pathname;
      const currentSelectedTab = selectedTabRef.current;
      const currentDrawerInfo = drawerInfoRef.current;

      // Don't show notification if user is on conversations page
      const isOnConversationsPage = currentPathname === `/workspace${WORKSPACE_ROUTES.CONVERSATIONS}`;

      // Don't show notification if drawer is open with ConversationTab selected and remittanceItemId matches
      const isConversationTabSelected = currentSelectedTab === TabType.CONVERSATIONS;
      const isMatchingRemittanceItem =
        isConversationTabSelected && currentDrawerInfo?.remittanceItemId === newConversationMessagePayload.referenceId;

      // Show notification only if user is not in conversation page or conversation tab
      return !isOnConversationsPage && !isMatchingRemittanceItem;
    },
    []
  );

  const showConversationNotification = useCallback(
    (newConversationMessagePayload: NewConversationMessagePayload) => {
      const isCurrentUserMentioned = newConversationMessagePayload.mentionedUsers?.some(
        mentionedUser => mentionedUser.email === user?.email
      );
      const isCurrentUserIsOwner = newConversationMessagePayload.sender.email === user?.email;
      if (!isCurrentUserMentioned || isCurrentUserIsOwner) {
        return;
      }

      const messageTitle = (
        <Flex direction="column" className="mb-2 font-regular">
          <Flex align="center" direction="row" className="m-0 text-gray-300 lh-1-25 mb-1">
            <Paragraph className="m-0 text-primary-600 me-1">
              Check #{newConversationMessagePayload.checkNumber}
            </Paragraph>
            <Icon name={Icons.ChevronRight} size="small" color="inherit" />
            <Paragraph className="m-0 text-gray-700 ms-1">{newConversationMessagePayload.invoiceNumber}</Paragraph>
          </Flex>
        </Flex>
      );
      const messageContent = (
        <Flex direction="column" className="font-regular">
          <Paragraph className="m-0">
            <strong>@{newConversationMessagePayload.sender.fullName}</strong> mentioned you
          </Paragraph>
          {newConversationMessagePayload.attachments && newConversationMessagePayload.attachments.length > 0 && (
            <Paragraph className="m-0">
              <strong>{newConversationMessagePayload.attachments.length}</strong> document
              {newConversationMessagePayload.attachments.length > 1 ? "s" : ""} attached
            </Paragraph>
          )}
        </Flex>
      );
      const notificationKey: Key = `new-conversation-message-${newConversationMessagePayload.id}` as Key;

      open({
        message: messageTitle,
        content: messageContent,
        placement: "bottomRight",
        key: notificationKey,
        className: "new-conversation-message",
        type: "info",
        closable: true,
        duration: 8,
        actionBar: (
          <Flex direction="row" align="center" justifyContent="end" className="mt-2">
            <Button variant="outline" size="small" color="grey" onPress={() => close(notificationKey)}>
              Dismiss
            </Button>
            <Button
              variant="solid"
              size="small"
              color="primary"
              onPress={() => handleViewConversation(notificationKey, newConversationMessagePayload)}
            >
              View
            </Button>
          </Flex>
        ),
      });
    },
    [user?.email, open, close, handleViewConversation]
  );

  // Set up WebSocket listener for new conversation messages
  useSocketListener({
    onProcessed: (wsResponse: string) => {
      const response = jsonSerializer.deserialize<WebSocketResponse<any>>(wsResponse);

      if (response.action === WebSocketAction.NEW_CONVERSATION) {
        const newConversationMessagePayload = new NewConversationMessagePayload(response.data);
        logger.info("newConversationMessagePayload", newConversationMessagePayload);
        emit(NotificationEvents.NEW_CONVERSATION_MESSAGE, newConversationMessagePayload, {
          onReject: () => {
            addOrUpdateConversationHandler(newConversationMessagePayload);
            if (shouldShowNotification(newConversationMessagePayload)) {
              showConversationNotification(newConversationMessagePayload);
              incrementNewMessageCount();
            }
          },
          onNoSubscribers: () => {
            addOrUpdateConversationHandler(newConversationMessagePayload);
            if (shouldShowNotification(newConversationMessagePayload)) {
              showConversationNotification(newConversationMessagePayload);
              incrementNewMessageCount();
            }
          },
        });
      }
    },
    enabled: true,
  });

  return {
    contextHolder,
  };
};
